import { create } from '@bufbuild/protobuf';
import {
  GenericServiceSecretDataSchema,
  SecretSchema,
  type GenericServiceSecretData,
  type Secret,
} from '@/proto/agentplatform/v2/secret_pb';

/** oneof 分支 case，即对象存储凭证的类型（无独立 type 字段）。 */
export type StorageTypeCase = 'aliyunOss' | 'volcengineTos';

export interface StorageTypeOption {
  value: StorageTypeCase;
  label: string;
}

export const STORAGE_TYPE_OPTIONS: StorageTypeOption[] = [
  { value: 'aliyunOss', label: '阿里云 OSS' },
  { value: 'volcengineTos', label: '火山引擎 TOS' },
];

const ENDPOINT_PLACEHOLDER: Record<StorageTypeCase, string> = {
  aliyunOss: 'https://oss-cn-hangzhou.aliyuncs.com',
  volcengineTos: 'https://tos-s3-cn-hangzhou.volces.com',
};

/** 据存储类型返回 Endpoint 输入框的示例值（仅作 placeholder 提示）。 */
export function endpointPlaceholder(storageType: StorageTypeCase): string {
  return ENDPOINT_PLACEHOLDER[storageType] ?? ENDPOINT_PLACEHOLDER.aliyunOss;
}

/**
 * 类型标签。入参放宽为 string 以兼容存量数据里已下线的分支（如旧的 `oss`
 * 通用对象存储）；未命中选项时回退返回原 case 值。
 */
export function storageTypeLabel(value: string): string {
  return STORAGE_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

/** 读凭证的类型标签（据选中的 oneof 分支）。 */
export function secretTypeLabel(secret: Secret): string {
  const c = secret.data.case;
  return c ? storageTypeLabel(c) : '-';
}

/** 取凭证的具体数据（endpoint/ak/sk/region/options）。 */
export function secretData(secret: Secret): GenericServiceSecretData | undefined {
  return secret.data.value;
}

export interface BuildSecretInput {
  name: string;
  storageType: StorageTypeCase;
  endpoint: string;
  region: string;
  accessKey: string;
  secretKey: string;
}

/** 组装用于 Create/Update 的 Secret（把类型落到 oneof 分支）。 */
export function buildSecret(input: BuildSecretInput): Secret {
  const body = create(GenericServiceSecretDataSchema, {
    endpoint: input.endpoint,
    region: input.region,
    accessKey: input.accessKey,
    secretKey: input.secretKey,
  });
  return create(SecretSchema, {
    name: input.name,
    data: { case: input.storageType, value: body },
  });
}
